import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { MessageSquare, Phone, Mail, Building2, MapPin, Clock, CheckCircle, XCircle, Loader } from 'lucide-react';

function AdminMatchRequests() {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [selectedReq, setSelectedReq] = useState(null);
    const [adminNote, setAdminNote] = useState('');
    const [updating, setUpdating] = useState(false);

    useEffect(() => { fetchRequests(); }, []);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'matchRequests'), orderBy('createdAt', 'desc'));
            const snap = await getDocs(q);
            const data = [];
            for (const d of snap.docs) {
                const req = { id: d.id, ...d.data() };
                // 치과 추가 정보 가져오기
                if (req.clinicId && req.clinicId !== 'guest') {
                    try {
                        const clinicDoc = await getDoc(doc(db, 'users', req.clinicId));
                        if (clinicDoc.exists()) {
                            const cd = clinicDoc.data();
                            req.clinicPhone = req.clinicPhone || cd.phone || '';
                            req.clinicEmail = req.clinicEmail || cd.email || '';
                            req.clinicAddress = cd.address || '';
                            // QR 웹 요청이면 폼 입력값 우선
                            if (req.source !== 'qr_web') {
                                req.clinicBizName = cd.businessName || cd.companyName || req.clinicName || '';
                            }
                        }
                    } catch (e) { /* skip */ }
                }
                // 기공소 추가 정보 가져오기
                if (req.labId) {
                    try {
                        const labDoc = await getDoc(doc(db, 'users', req.labId));
                        if (labDoc.exists()) {
                            const ld = labDoc.data();
                            req.labPhone = ld.phone || '';
                            req.labEmail = ld.email || '';
                            req.labAddress = ld.address || '';
                            req.labBizName = ld.businessName || ld.companyName || req.labName || '';
                        }
                        // 기공소 광고 정보
                        const adDoc = await getDoc(doc(db, 'labAdvertisements', req.labId));
                        if (adDoc.exists()) {
                            const ad = adDoc.data();
                            req.labContactPhone = ad.contactPhone || '';
                            req.labContactEmail = ad.contactEmail || '';
                            req.labSlogan = ad.slogan || '';
                        }
                    } catch (e) { /* skip */ }
                }
                data.push(req);
            }
            setRequests(data);
        } catch (e) {
            console.error('문의 로드 실패:', e);
        }
        setLoading(false);
    };

    const updateStatus = async (reqId, newStatus) => {
        setUpdating(true);
        try {
            await updateDoc(doc(db, 'matchRequests', reqId), {
                status: newStatus,
                adminNotes: adminNote || '',
                updatedAt: serverTimestamp(),
            });
            setRequests(prev => prev.map(r => r.id === reqId ? { ...r, status: newStatus, adminNotes: adminNote } : r));
            setSelectedReq(null);
            setAdminNote('');
        } catch (e) {
            console.error('상태 변경 실패:', e);
            alert('상태 변경에 실패했습니다.');
        }
        setUpdating(false);
    };

    const fmtDate = (ts) => {
        if (!ts) return '-';
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    };

    const statusLabel = { pending: '대기', consulting: '상담중', matched: '매칭완료', rejected: '반려' };
    const statusColor = { pending: '#f59e0b', consulting: '#3b82f6', matched: '#10b981', rejected: '#ef4444' };
    const statusBg = { pending: '#fffbeb', consulting: '#eff6ff', matched: '#f0fdf4', rejected: '#fef2f2' };

    const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
    const pendingCount = requests.filter(r => r.status === 'pending').length;

    if (loading) return <div style={S.loading}>로딩 중...</div>;

    return (
        <div style={S.container}>
            <div style={S.header}>
                <h1 style={S.title}>거래 문의 관리</h1>
                {pendingCount > 0 && <span style={S.pendingBadge}>{pendingCount}건 대기중</span>}
            </div>

            {/* 필터 */}
            <div style={S.filterRow}>
                {[['all','전체'], ['pending','대기'], ['consulting','상담중'], ['matched','완료'], ['rejected','반려']].map(([val, label]) => (
                    <button key={val} onClick={() => setFilter(val)}
                        style={{ ...S.filterBtn, ...(filter === val ? S.filterBtnActive : {}) }}>
                        {label} {val !== 'all' && <span style={S.filterCount}>{requests.filter(r => val === 'all' || r.status === val).length}</span>}
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <div style={S.empty}><MessageSquare size={40} color="#d1d5db" /><p>문의가 없습니다.</p></div>
            ) : (
                <div style={S.tableWrap}>
                    <table style={S.table}>
                        <thead>
                            <tr>
                                <th style={S.th}>상태</th>
                                <th style={S.th}>치과</th>
                                <th style={S.th}>전화번호</th>
                                <th style={S.th}>기공소</th>
                                <th style={S.th}>기공소 연락처</th>
                                <th style={S.th}>문의 내용</th>
                                <th style={S.th}>접수일</th>
                                <th style={S.th}>처리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((req, i) => (
                                <tr key={req.id} style={{ ...S.tr, backgroundColor: i % 2 ? '#f8fafc' : '#fff' }}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#eef2ff'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = i % 2 ? '#f8fafc' : '#fff'}>
                                    <td style={S.td}>
                                        <span style={{ ...S.statusBadge, backgroundColor: statusBg[req.status] || '#f1f5f9', color: statusColor[req.status] || '#64748b' }}>
                                            {statusLabel[req.status] || req.status}
                                        </span>
                                    </td>
                                    <td style={S.td}>
                                        <div style={S.cellName}>{req.clinicCompany || req.clinicBizName || req.clinicName}</div>
                                        {req.clinicCompany && req.clinicName && <div style={S.cellSub}>담당: {req.clinicName}</div>}
                                        {!req.clinicCompany && req.clinicName && req.clinicBizName && req.clinicName !== req.clinicBizName && <div style={S.cellSub}>{req.clinicName}</div>}
                                        {req.clinicEmail && <div style={S.cellSub}>{req.clinicEmail}</div>}
                                    </td>
                                    <td style={S.td}>
                                        {req.clinicPhone ? (
                                            <a href={`tel:${req.clinicPhone}`} style={S.phoneLink}><Phone size={12} /> {req.clinicPhone}</a>
                                        ) : '-'}
                                    </td>
                                    <td style={S.td}>
                                        <div style={S.cellName}>{req.labBizName || req.labName}</div>
                                        {req.labSlogan && <div style={S.cellSub}>{req.labSlogan}</div>}
                                    </td>
                                    <td style={S.td}>
                                        {req.labContactPhone || req.labPhone ? (
                                            <a href={`tel:${req.labContactPhone || req.labPhone}`} style={S.phoneLink}><Phone size={12} /> {req.labContactPhone || req.labPhone}</a>
                                        ) : '-'}
                                        {(req.labContactEmail || req.labEmail) && <div style={S.cellSub}>{req.labContactEmail || req.labEmail}</div>}
                                    </td>
                                    <td style={{ ...S.td, maxWidth: 200 }}>
                                        <div style={S.msgText}>{req.message || '-'}</div>
                                    </td>
                                    <td style={S.td}>{fmtDate(req.createdAt)}</td>
                                    <td style={S.td}>
                                        <button onClick={() => { setSelectedReq(req); setAdminNote(req.adminNotes || ''); }}
                                            style={S.detailBtn}>상세</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* 상세 모달 */}
            {selectedReq && (
                <div style={S.overlay} onClick={() => setSelectedReq(null)}>
                    <div style={S.modal} onClick={e => e.stopPropagation()}>
                        <h2 style={S.modalTitle}>문의 상세</h2>

                        {/* 치과 정보 */}
                        <div style={S.infoCard}>
                            <h4 style={S.infoTitle}><Building2 size={16} color="#3b82f6" /> 요청자 정보</h4>
                            <div style={S.infoGrid}>
                                <div style={S.infoItem}><span style={S.infoLabel}>이름</span><span>{selectedReq.clinicName || '-'}</span></div>
                                <div style={S.infoItem}><span style={S.infoLabel}>업체명</span><span>{selectedReq.clinicCompany || selectedReq.clinicBizName || '-'}</span></div>
                                <div style={S.infoItem}><span style={S.infoLabel}>전화</span>
                                    {selectedReq.clinicPhone ? <a href={`tel:${selectedReq.clinicPhone}`} style={S.phoneLink}><Phone size={12} /> {selectedReq.clinicPhone}</a> : '-'}
                                </div>
                                <div style={S.infoItem}><span style={S.infoLabel}>이메일</span><span>{selectedReq.clinicEmail || '-'}</span></div>
                                {selectedReq.clinicAddress && <div style={S.infoItem}><span style={S.infoLabel}>주소</span><span>{selectedReq.clinicAddress}</span></div>}
                                {selectedReq.source && <div style={S.infoItem}><span style={S.infoLabel}>경로</span><span>{selectedReq.source === 'qr_web' ? 'QR 웹' : '앱/웹'}</span></div>}
                            </div>
                        </div>

                        {/* 기공소 정보 */}
                        <div style={S.infoCard}>
                            <h4 style={S.infoTitle}><Building2 size={16} color="#6366f1" /> 기공소 정보</h4>
                            <div style={S.infoGrid}>
                                <div style={S.infoItem}><span style={S.infoLabel}>기공소명</span><span>{selectedReq.labBizName || selectedReq.labName}</span></div>
                                <div style={S.infoItem}><span style={S.infoLabel}>전화</span>
                                    {(selectedReq.labContactPhone || selectedReq.labPhone) ? <a href={`tel:${selectedReq.labContactPhone || selectedReq.labPhone}`} style={S.phoneLink}><Phone size={12} /> {selectedReq.labContactPhone || selectedReq.labPhone}</a> : '-'}
                                </div>
                                <div style={S.infoItem}><span style={S.infoLabel}>이메일</span><span>{selectedReq.labContactEmail || selectedReq.labEmail || '-'}</span></div>
                                <div style={S.infoItem}><span style={S.infoLabel}>주소</span><span>{selectedReq.labAddress || '-'}</span></div>
                            </div>
                        </div>

                        {/* 문의 내용 */}
                        <div style={S.infoCard}>
                            <h4 style={S.infoTitle}><MessageSquare size={16} color="#f59e0b" /> 문의</h4>
                            {selectedReq.clinicName && (
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontSize: '12px', color: '#6366f1', fontWeight: '600' }}>이름: </span>
                                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{selectedReq.clinicName}</span>
                                </div>
                            )}
                            {selectedReq.clinicCompany && (
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontSize: '12px', color: '#6366f1', fontWeight: '600' }}>업체명: </span>
                                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{selectedReq.clinicCompany}</span>
                                </div>
                            )}
                            <p style={S.modalMsg}>{selectedReq.message || '내용 없음'}</p>
                            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                                접수: {fmtDate(selectedReq.createdAt)}
                                {selectedReq.source === 'qr_web' && <span style={{ marginLeft: '8px', padding: '2px 6px', backgroundColor: '#eef2ff', color: '#6366f1', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>QR 웹</span>}
                            </div>
                        </div>

                        {/* 관리자 메모 */}
                        <div style={S.infoCard}>
                            <h4 style={S.infoTitle}>관리자 메모</h4>
                            <textarea value={adminNote} onChange={e => setAdminNote(e.target.value)}
                                placeholder="처리 내용을 기록하세요..." rows={3} style={S.textarea} />
                        </div>

                        {/* 상태 변경 버튼 */}
                        <div style={S.actionRow}>
                            <button onClick={() => updateStatus(selectedReq.id, 'consulting')} disabled={updating}
                                style={{ ...S.actionBtn, backgroundColor: '#3b82f6', color: '#fff' }}>
                                <Loader size={14} /> 상담중
                            </button>
                            <button onClick={() => updateStatus(selectedReq.id, 'matched')} disabled={updating}
                                style={{ ...S.actionBtn, backgroundColor: '#10b981', color: '#fff' }}>
                                <CheckCircle size={14} /> 매칭완료
                            </button>
                            <button onClick={() => updateStatus(selectedReq.id, 'rejected')} disabled={updating}
                                style={{ ...S.actionBtn, backgroundColor: '#ef4444', color: '#fff' }}>
                                <XCircle size={14} /> 반려
                            </button>
                            <button onClick={() => setSelectedReq(null)} style={{ ...S.actionBtn, backgroundColor: '#f1f5f9', color: '#64748b' }}>
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const S = {
    container: { padding: '20px', maxWidth: 1200, margin: '0 auto' },
    loading: { textAlign: 'center', padding: '60px 0', color: '#94a3b8' },
    header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
    title: { margin: 0, fontSize: 22, fontWeight: 700, color: '#0f172a' },
    pendingBadge: { padding: '4px 12px', backgroundColor: '#fef3c7', color: '#92400e', borderRadius: 20, fontSize: 13, fontWeight: 600 },
    filterRow: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' },
    filterBtn: { padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: 8, backgroundColor: '#fff', fontSize: 13, fontWeight: 600, color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 },
    filterBtnActive: { backgroundColor: '#6366f1', color: '#fff', borderColor: '#6366f1' },
    filterCount: { fontSize: 11, opacity: 0.7 },
    empty: { textAlign: 'center', padding: '60px 0', color: '#94a3b8' },
    tableWrap: { backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { padding: '10px 10px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', backgroundColor: '#f8fafc' },
    tr: { borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.15s' },
    td: { padding: '10px 10px', fontSize: 13, color: '#334155', verticalAlign: 'top' },
    statusBadge: { padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700 },
    cellName: { fontWeight: 600, color: '#0f172a', fontSize: 13 },
    cellSub: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
    phoneLink: { display: 'flex', alignItems: 'center', gap: 4, color: '#3b82f6', textDecoration: 'none', fontSize: 13, fontWeight: 500 },
    msgText: { fontSize: 12, color: '#64748b', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
    detailBtn: { padding: '5px 12px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
    modal: { backgroundColor: '#fff', borderRadius: 16, padding: 24, maxWidth: 600, width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
    modalTitle: { margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#0f172a' },
    infoCard: { padding: 16, backgroundColor: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 12 },
    infoTitle: { display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#0f172a' },
    infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
    infoItem: { display: 'flex', flexDirection: 'column', gap: 2 },
    infoLabel: { fontSize: 11, color: '#94a3b8', fontWeight: 600 },
    modalMsg: { margin: 0, fontSize: 14, color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' },
    textarea: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
    actionRow: { display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' },
    actionBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};

export default AdminMatchRequests;
